import { useState } from "react";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { authApi } from "@/lib/api";
import { ShaderAnimation } from "@/components/ui/shader-animation";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authApi.login(form);
      localStorage.setItem("auth_token", res.data.token);
      localStorage.setItem("auth_user", JSON.stringify(res.data.user));
      const role = res.data.user.role;
      if (role === "admin") {
        toast.success("Welcome back, Admin!");
        window.location.href = "/admin";
      } else if (role === "user") {
        toast.success(`Welcome, ${res.data.user.name || res.data.user.username}!`);
        window.location.href = "/user";
      } else if (role === "product") {
        toast.success("Welcome to SilverLine — Product view");
        window.location.href = "/product";
      } else {
        toast.success(`Welcome, ${res.data.user.partner_name || res.data.user.username}!`);
        window.location.href = "/partner";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="login-container">
      <div className="login-visual" style={{ position: 'relative' }}>
        <ShaderAnimation />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'left', zIndex: 10, pointerEvents: 'none' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_partner-earnings-hub/artifacts/nzmmhqpq_CloudTV_BlueColor_%201Nov%202019_WW.png"
            alt="CloudTV"
            style={{ height: '48px', margin: '0 0 10px', opacity: 0.7 }}
            data-testid="cloudtv-logo"
          />
          <h1 className="text-white -mt-1" style={{ fontFamily: "'Shrikhand', cursive", fontSize: '5rem', lineHeight: 1, letterSpacing: '-0.02em', textShadow: '0 2px 24px rgba(0,0,0,0.35)' }} data-testid="silverline-title">
            SilverLine
          </h1>
          <p className="text-white/80 text-base mt-3 tracking-wide" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", textShadow: '0 1px 10px rgba(0,0,0,0.4)' }}>
            A revenue share program for CloudTV's ecosystem partners
          </p>
        </div>
      </div>

      <div className="login-form-side">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Sign in to your account
            </h2>
            <p className="text-slate-400 mt-1 text-sm">Enter your credentials to access the dashboard</p>
          </div>

          {/* Google Sign In */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            className="w-full h-11 border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-lg transition-all mb-6"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 text-slate-400">or sign in with credentials</span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 font-medium text-sm">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                placeholder="Enter your username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="h-11 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium text-sm">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-11 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all active:scale-95"
            >
              {loading ? "Signing in..." : "Sign In"}
              {!loading && <LogIn size={16} className="ml-2" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
